import { TreePine, Phone, Mail, MapPin, Award, Shield } from "lucide-react";
import { Link } from "react-router-dom";

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const services = [
    "Tree Trimming",
    "Tree Removal",
    "Stump Grinding",
    "Fire Safety & Brush Removal",
    "ISA Arborist Assessment",
    "Emergency Service",
    "HOA & Commercial",
  ];

  const serviceAreas = [
    "Paradise Valley, AZ 85253",
    "Paradise Valley, AZ 85254",
    "Paradise Valley, AZ 85255",
    "Scottsdale, AZ 85260",
    "Scottsdale, AZ 85250",
    "Scottsdale, AZ 85251",
    "And 15+ more zip codes",
  ];

  return (
    <footer className="footer py-20" data-testid="footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-12 mb-16">
          {/* Company Info */}
          <div>
            <div className="flex items-center gap-3 mb-8">
              <div className="w-12 h-12 bg-gradient-to-br from-[#10B981] to-[#059669] flex items-center justify-center">
                <TreePine className="w-6 h-6 text-white" strokeWidth={2.5} />
              </div>
              <div>
                <p className="font-['Cormorant_Garamond'] text-xl font-bold text-white">
                  CELEBRITY <span className="text-[#10B981]">TREE</span>
                </p>
                <p className="text-[10px] tracking-[0.2em] text-[#D4A853] font-['Cinzel']">
                  SERVICE LLC
                </p>
              </div>
            </div>
            <p className="text-[#94A3B8] text-sm mb-8 leading-relaxed">
              ISA Certified Arborists specializing in premium tree care,
              wildfire prevention, and luxury property maintenance since 2015.
            </p>
            <div className="space-y-4">
              <a
                href="tel:4803517775"
                className="flex items-center gap-3 text-[#94A3B8] hover:text-[#10B981] transition-colors"
              >
                <div className="w-8 h-8 bg-[#10B981]/20 flex items-center justify-center">
                  <Phone className="w-4 h-4 text-[#10B981]" />
                </div>
                (480) 351-7775
              </a>
              <a
                href="mailto:info@paradisevalleytreeservice.com"
                className="flex items-center gap-3 text-[#94A3B8] hover:text-[#10B981] transition-colors"
              >
                <div className="w-8 h-8 bg-[#10B981]/20 flex items-center justify-center">
                  <Mail className="w-4 h-4 text-[#10B981]" />
                </div>
                info@paradisevalleytreeservice.com
              </a>
              <div className="flex items-center gap-3 text-[#94A3B8]">
                <div className="w-8 h-8 bg-[#10B981]/20 flex items-center justify-center">
                  <MapPin className="w-4 h-4 text-[#10B981]" />
                </div>
                Paradise Valley, AZ
              </div>
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-white font-bold mb-6 text-sm uppercase tracking-wider">Our Services</h3>
            <ul className="space-y-3">
              {services.map((service, index) => (
                <li key={index}>
                  <a
                    href="#services"
                    className="text-[#94A3B8] hover:text-[#10B981] transition-colors text-sm"
                  >
                    {service}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas - SEO Internal Links */}
          <div>
            <h3 className="text-white font-bold mb-6 text-sm uppercase tracking-wider">Service Areas</h3>
            <div className="mb-4">
              <p className="text-[#D4A853] text-xs uppercase tracking-wider font-bold mb-3">Paradise Valley</p>
              <ul className="space-y-2">
                {[
                  { name: "Tree Trimming", href: "/tree-trimming-paradise-valley" },
                  { name: "Tree Removal", href: "/tree-removal-paradise-valley" },
                  { name: "Tree Service", href: "/tree-service-paradise-valley" },
                  { name: "Arborist", href: "/arborist-paradise-valley" },
                  { name: "Stump Grinding", href: "/stump-grinding-paradise-valley" },
                ].map((link, i) => (
                  <li key={i}>
                    <Link to={link.href} className="text-[#94A3B8] hover:text-[#10B981] transition-colors text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
            <div>
              <p className="text-[#D4A853] text-xs uppercase tracking-wider font-bold mb-3">Scottsdale</p>
              <ul className="space-y-2">
                {[
                  { name: "Tree Trimming", href: "/tree-trimming-scottsdale" },
                  { name: "Tree Removal", href: "/tree-removal-scottsdale" },
                  { name: "Tree Service", href: "/tree-service-scottsdale" },
                  { name: "Tree Care", href: "/tree-care-scottsdale" },
                  { name: "Stump Grinding", href: "/stump-grinding-scottsdale" },
                ].map((link, i) => (
                  <li key={i}>
                    <Link to={link.href} className="text-[#94A3B8] hover:text-[#10B981] transition-colors text-sm">
                      {link.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Quick Links & Certifications */}
          <div>
            <h3 className="text-white font-bold mb-6 text-sm uppercase tracking-wider">Quick Links</h3>
            <ul className="space-y-3 mb-8">
              {["Services", "Fire Safety", "About Us", "Service Areas", "Pricing", "Contact"].map((link, index) => (
                <li key={index}>
                  <a
                    href={`#${link.toLowerCase().replace(" ", "-")}`}
                    className="text-[#94A3B8] hover:text-[#10B981] transition-colors text-sm"
                  >
                    {link}
                  </a>
                </li>
              ))}
              <li>
                <Link
                  to="/careers"
                  className="text-[#D4A853] hover:text-[#10B981] transition-colors text-sm font-medium"
                  data-testid="footer-careers-link"
                >
                  Careers — Join Our Team
                </Link>
              </li>
            </ul>

            {/* Certifications */}
            <div className="p-5 bg-[#142238] border border-[#10B981]/20">
              <div className="flex items-center gap-3 mb-3">
                <Award className="w-5 h-5 text-[#D4A853]" />
                <p className="text-[#D4A853] text-xs uppercase tracking-wider font-bold">
                  Certifications
                </p>
              </div>
              <p className="text-white text-sm font-semibold">ISA Certified Arborists</p>
              <p className="text-[#94A3B8] text-xs">
                Licensed & Fully Insured
              </p>
            </div>

            {/* Arizona Pride */}
            <div className="mt-4 p-5 bg-[#142238] border border-[#10B981]/20">
              <div className="flex items-center gap-3">
                {/* Arizona State Flag */}
                <svg
                  viewBox="0 0 60 40"
                  className="w-10 h-7 flex-shrink-0"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="60" height="40" fill="#002868"/>
                  <polygon points="0,0 30,20 60,0" fill="#BF0A30"/>
                  <g fill="#FED700">
                    <polygon points="30,20 26,9 34,9"/>
                    <polygon points="30,20 22,12 38,12"/>
                    <polygon points="30,20 18,8 42,8"/>
                    <polygon points="30,20 16,6 44,6"/>
                  </g>
                  <circle cx="30" cy="20" r="5" fill="#FED700"/>
                </svg>
                <div>
                  <p className="text-white text-sm font-semibold">Locally Owned & Operated</p>
                  <p className="text-[#94A3B8] text-xs">Paradise Valley, Arizona</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Section Divider */}
        <div className="section-divider mb-8"></div>

        {/* Bottom Bar */}
        <div className="flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-4">
            {/* Small Arizona Flag */}
            <svg
              viewBox="0 0 60 40"
              className="w-8 h-5"
              xmlns="http://www.w3.org/2000/svg"
            >
              <rect width="60" height="40" fill="#002868"/>
              <polygon points="0,0 30,20 60,0" fill="#BF0A30"/>
              <g fill="#FED700">
                <polygon points="30,20 26,9 34,9"/>
                <polygon points="30,20 22,12 38,12"/>
                <polygon points="30,20 18,8 42,8"/>
                <polygon points="30,20 16,6 44,6"/>
              </g>
              <circle cx="30" cy="20" r="5" fill="#FED700"/>
            </svg>
            <p className="text-[#64748B] text-sm">
              © {currentYear} Celebrity Tree Service LLC. All rights reserved.
            </p>
          </div>
          <p className="text-[#64748B] text-sm">
            Locally Owned & Operated | Paradise Valley, Arizona
          </p>
        </div>
      </div>
    </footer>
  );
}
